import { IClientOrder } from "interfaces/IClientOrders";
import { Col, Row } from "react-bootstrap";
import ReviewModal from "./ReviewModal";
import { useAuthStore } from "../../../../../zustand/loginStore";
import React, { useState, useEffect } from "react";
import { getMyReview, createAuthenticatedAxiosInstance } from "api/auth";
import { getOrderProductImage } from "../../../../../utils/imagesAndColors";

/**
 * Custom props for the client order card
 */
interface Props {
  /**
   * The order data
   */
  order: IClientOrder;
  /**
   * show order details
   */
  handleOrderDetails: (order: IClientOrder) => void;
  /**
   * Whether the component is in mobile view
   */
  isMobile?: boolean;
}

/**
 * Card component containing the user order data
 * @returns
 */
export const ClientOrderCard = ({ order, handleOrderDetails, isMobile = false }: Props) => {
  const firstProduct = order?.order_products?.[0];
  const correctImage = firstProduct ? getOrderProductImage(firstProduct) : '';
  const { product_name, size, colorName } = firstProduct || {};
  const totalProducts = order?.order_products?.reduce((sum, product) => sum + (product.quantity || 0), 0) || 0;
  const { total, status } = order;
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [allProductsReviewed, setAllProductsReviewed] = useState(false);
  const [checkingReviews, setCheckingReviews] = useState(false);
  const { userData, token } = useAuthStore();

  // Verificar si todos los productos ya están calificados
  useEffect(() => {
    if (status === "Entregado" && order.order_products.length > 0) {
      setCheckingReviews(true);
      const axiosInstance = createAuthenticatedAxiosInstance(token);

      const checkAllReviews = async () => {
        try {
          const reviewPromises = order.order_products.map((product) =>
            getMyReview(axiosInstance, product.product_id)
          );

          const reviews = await Promise.all(reviewPromises);
          const allReviewed = reviews.every((review) => review !== null);
          setAllProductsReviewed(allReviewed);
        } catch (error) {
          console.error("Error checking reviews:", error);
          setAllProductsReviewed(false);
        } finally {
          setCheckingReviews(false);
        }
      };

      checkAllReviews();
    }
  }, [order.order_products, status, token]);

  /**
   * Function passed to parent component to show an order details
   */
  const onDetails = (order: IClientOrder) => handleOrderDetails(order);

  if (isMobile) {
    return (
      <div className="client-order-card mobile-order-card my-3">
        <div className="d-flex align-items-start">
          <img
            className="mobile-order-image"
            src={correctImage}
            alt={product_name || 'Producto'}
          />
          <div className="flex-grow-1">
            <h6 className="mobile-order-title">{product_name || 'Producto no disponible'}</h6>
            <div className="mobile-order-status">{status}</div>
            <div className="mobile-order-price">${total}</div>
          </div>
        </div>
        <div className="text-center mt-3">
          <div
            className="mobile-order-details-link"
            onClick={() => onDetails(order)}
          >
            Ver detalles de compra
          </div>
        </div>
        {showReviewModal && userData?.id && (
          <ReviewModal
            orderProducts={order.order_products}
            userId={userData.id}
            onClose={() => setShowReviewModal(false)}
          />
        )}
      </div>
    );
  }

  return (
    <div className="d-flex client-order-card my-4 align-content-center">
      <img
        className="client-order-image me-4 align-self-center"
        src={correctImage}
        alt={product_name || 'Producto'}
      />
      <Row className="flex-grow-1">
        <Col className="">
          <h5>{product_name || 'Producto no disponible'}</h5>
          <div className="d-flex gap-2">
            <h5>Talla:</h5> <h5>{size || 'N/A'}</h5>
          </div>
          <div className="d-flex gap-2">
            <h5>Color:</h5> <h5>{colorName || 'N/A'}</h5>
          </div>
          <div className="d-flex gap-2">
            <h5>Total de compra:</h5>{" "}
            <h4 className="client-order-price">${total}</h4>
          </div>
        </Col>
        <Col className="d-flex flex-column justify-content-between">
          <div>
            <h5>{status}</h5>
            <div className="d-flex gap-2">
              <h5>Total de productos:</h5> <h5>{totalProducts}</h5>
            </div>
          </div>
          {status === "Entregado" && (
            <h5
              className="client-order-link"
              onClick={() => setShowReviewModal(true)}
              style={{
                textDecoration: "underline",
                cursor: "pointer",
              }}
            >
              Calificar
            </h5>
          )}

          <h5
            className="client-order-link"
            style={{
              textDecoration: "underline",
              cursor: "pointer",
            }}
            onClick={() => onDetails(order)}
          >
            Ver detalles de compra
          </h5>
        </Col>
      </Row>
      {showReviewModal && userData?.id && (
        <ReviewModal
          orderProducts={order.order_products}
          userId={userData.id}
          onClose={() => setShowReviewModal(false)}
        />
      )}
    </div>
  );
};
